import React, { useMemo, useState } from "react";
import { Plus, Minus, Heart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";
import { ProductImage } from "@/components/ProductImage";

export function ProductCard({ variants, popularIds = [], favIds = [], onToggleFav, index = 0 }) {
  const { getQty, addItem, setQty } = useCart();

  const sorted = useMemo(() => [...variants].sort((a, b) => a.price - b.price), [variants]);
  const defaultId = (sorted.find((v) => v.available) || sorted[0]).product_id;
  const [selId, setSelId] = useState(defaultId);
  const sel = sorted.find((v) => v.product_id === selId) || sorted[0];

  const hasSizes = sorted.length > 1;
  const qty = getQty(sel.product_id);
  const soldOut = !sel.available;
  const isFavourite = favIds.includes(sel.product_id);
  const popular = popularIds.includes(sel.product_id);

  return (
    <div
      className="relative bg-white rounded-[16px] p-2.5 border border-[#F5F8F6] shadow-[0_4px_24px_rgba(11,122,59,0.06)] pm-fade-up flex flex-col"
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
      data-testid={`product-card-${sel.product_id}`}
    >
      <div className="relative rounded-[12px] overflow-hidden aspect-square bg-[#F5F8F6]">
        <ProductImage src={sel.image} alt={sel.name} className="w-full h-full object-cover" iconSize={34} />
        {popular && (
          <span className="absolute top-2 left-2 bg-[#C0392B] text-white text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5">
            Popular
          </span>
        )}
        <button
          onClick={() => onToggleFav?.(sel)}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center active:scale-90 transition-transform"
          data-testid={`fav-btn-${sel.product_id}`}
          aria-label="favourite"
        >
          <Heart size={16} className={isFavourite ? "fill-[#C0392B] text-[#C0392B]" : "text-[#6B8375]"} />
        </button>
        {soldOut && (
          <div className="absolute inset-0 bg-[#122117]/55 flex items-center justify-center">
            <span className="bg-white text-[#122117] text-xs uppercase tracking-wider font-bold rounded-full px-3 py-1">
              Sold out
            </span>
          </div>
        )}
      </div>

      <div className="mt-2 px-0.5 flex flex-col flex-1">
        <h3 className="font-bold text-[13px] text-[#122117] leading-snug line-clamp-2 min-h-[34px]">{sel.name}</h3>

        {hasSizes ? (
          <div className="flex flex-wrap gap-1 mt-1" data-testid={`sizes-${sel.name}`}>
            {sorted.map((v) => {
              const active = v.product_id === sel.product_id;
              return (
                <button
                  key={v.product_id}
                  onClick={() => setSelId(v.product_id)}
                  data-testid={`size-${v.product_id}`}
                  className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border transition-colors ${
                    active
                      ? "bg-[#0B7A3B] text-white border-[#0B7A3B]"
                      : v.available
                      ? "bg-white text-[#122117] border-[#DDF0E5]"
                      : "bg-[#F5F8F6] text-[#6B8375] border-[#F5F8F6] line-through"
                  }`}
                >
                  {v.unit}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-[11px] text-[#6B8375] mt-0.5">{sel.unit}</p>
        )}

        <div className="mt-auto pt-1.5 flex items-center justify-between">
          <span className="font-extrabold text-[#0B7A3B]" data-testid={`price-${sel.product_id}`}>{money(sel.price)}</span>
          {soldOut ? (
            <span className="text-[11px] text-[#6B8375] font-semibold">Unavailable</span>
          ) : qty === 0 ? (
            <button
              onClick={() => addItem(sel)}
              className="w-9 h-9 rounded-full bg-[#0B7A3B] text-white flex items-center justify-center active:scale-90 transition-transform shadow-md hover:bg-[#095F2E]"
              data-testid={`add-btn-${sel.product_id}`}
            >
              <Plus size={18} />
            </button>
          ) : (
            <div className="flex items-center gap-1.5 bg-[#DDF0E5] rounded-full p-0.5 pm-pop" data-testid={`stepper-${sel.product_id}`}>
              <button
                onClick={() => setQty(sel.product_id, qty - 1)}
                className="w-7 h-7 rounded-full bg-white text-[#0B7A3B] flex items-center justify-center active:scale-90 transition-transform"
                data-testid={`dec-btn-${sel.product_id}`}
              >
                <Minus size={14} />
              </button>
              <span className="font-extrabold text-sm text-[#0B7A3B] min-w-[16px] text-center tabular-nums">{qty}</span>
              <button
                onClick={() => setQty(sel.product_id, qty + 1)}
                className="w-7 h-7 rounded-full bg-[#0B7A3B] text-white flex items-center justify-center active:scale-90 transition-transform"
                data-testid={`inc-btn-${sel.product_id}`}
              >
                <Plus size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
